import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Flex } from "antd";
import { ColumnsType } from "antd/es/table";
import { CurrencySelect } from "@/components/Selector";

import SText from "@/styles/SText";
import { Supplier, SupplierPrice } from "@/types/purchase";
import { dateFormat, priceFormat } from "@/utils/format";
import dayjs from "dayjs";
import { CustomerTabType } from "..";
import IconFolder from "@/assets/images/fileIcon/folder.svg";
import { divided, noRateAmount } from "@/utils/calc";


export type UsePriceColumn = (props: {
  onDetail: (params: SupplierPrice | null) => void;
  onDel: (id: string, delId: string) => void;
  onOpenTab: (type: CustomerTabType, item: { docId: string, shortName: string, code: string, proCode: string }) => void;
  supplier?: Supplier;

}) => ColumnsType<SupplierPrice>

const usePriceColumn: UsePriceColumn = ({ onDetail, onDel, onOpenTab, supplier }) => {
  const { t } = useTranslation();

  const columns: ColumnsType<SupplierPrice> = useMemo(() => [
    {
      title: <SText text={t("product.code")} />,
      key: "code",
      dataIndex: ["product", "code"],
      width: 100,
      fixed: "left",
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => (
        <SText key={item.id + "code"} text={item.product?.code ?? ""} />
      ),
    },
    {
      title: <SText text={t("product.name")} />,
      key: "name",
      width: 120,
      fixed: "left",
      render: (_, item) => (
        <SText key={item.id + "name"} text={item.product?.name ?? ""} />
      ),
    },
    {
      title: <SText text={t("order.brand")} />,
      key: "brand1",
      width: 180,
      render: (_, item) => <SText text={item.product?.model?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 80,
      render: (_, item) => <SText text={item.product?.uom?.toLocaleUpperCase() ?? ""} />,
    },
    {
      title: <SText text={t("purchase.brand")} />,
      key: "brand",
      width: 80,
      render: (_, item) => <SText text={item?.brand ?? ""} />,
    },
    {
      title: <SText text={t("purchase.mpn")} />,
      key: "mpn",
      width: 80,
      render: (_, item) => <SText text={item?.mpn ?? ""} />,
    },
    {
      title: <SText text={t("purchase.dcc")} />,
      key: "dcc",
      width: 110,
      render: (_, item) => <SText text={item?.dcc ?? ''} />,
    },
    {
      title: <SText text={t("purchase.netWeight")} suffix="(kg)" />,
      key: "netWeight",
      width: 80,
      render: (_, item) => <SText text={item?.netWeight ?? ''} />,
    },
    {
      title: <SText text={t("purchase.grossWeight")} suffix="(kg)" />,
      key: "grossWeight",
      width: 80,
      render: (_, item) => <SText text={item?.grossWeight ?? ''} />,
    },
    {
      title: <SText text={t("purchase.stPrice")} />,
      key: "price",
      dataIndex: "price",
      width: 80,
      sorter: (a, b) => Number(a?.price ?? 0) - Number(b?.price ?? 0),
      render: (_, item) => <SText text={priceFormat(item.price ?? "")} />,
    },
    {
      title: <SText text={t("purchase.mpq")} />,
      key: "mpq",
      dataIndex: "mpq",
      width: 80,
      render: (_, item) => <SText text={item.mpq ?? ''} />,
    },
    {
      title: <SText text={t("purchase.days")} />,
      key: "days",
      dataIndex: "days",
      width: 80,
      render: (_, item) => <SText text={item.days?.toString() ?? ''} />,
    },
    {
      title: <SText text={t("purchase.taxRate")} />,
      key: "taxRate",
      dataIndex: "taxRate",
      width: 90,
      sorter: (a, b) => {
        let ta = Number((a?.taxRate ?? supplier?.taxRate ?? a?.supplier?.taxRate) ?? 0);
        let tb = Number((b?.taxRate ?? supplier?.taxRate ?? b?.supplier?.taxRate) ?? 0);
        return ta - tb;
      },
      render: (_, item) => <SText text={item.taxRate || supplier?.taxRate || item?.supplier?.taxRate || ""} suffix="%"/>,
    },
    {
      title: <SText text={t("purchase.unRatePrice")} />,
      key: "unRatePrice",
      width: 110,
      render: (_, item) => {
        const taxRate = item.taxRate || supplier?.taxRate;
        return (
          <SText text={taxRate ? priceFormat(noRateAmount(item.price ?? "", divided(taxRate, "100"))) : ""} />
        );
      },
    },
    {
      title: <SText text={`${t("purchase.effectiveAt")}`} />,
      key: "effectiveAt",
      width: 100,
      render: (_, item) => <span>{item.effectiveAt ? dateFormat(dayjs(item.effectiveAt)) : ''}</span>,
    },
    {
      title: <SText text={`${t("purchase.expiredAtEcl")}`} />,
      key: "expiredAt",
      width: 100,
      render: (_, item) => <span>{item.expiredAt ? dateFormat(dayjs(item.expiredAt)) : ''}</span>,
    },
    {
      title: <SText text={t("purchase.currency")} />,
      dataIndex: "currency",
      width: 90,
      sorter: (a, b) => {
        const valA = a?.currency ?? "";
        const valB = b?.currency ?? "";
        return valA.localeCompare(valB);
      },
      render: (val, item) => (
        <CurrencySelect key={item.id} value={val || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("purchase.remark")} />,
      key: "remark",
      width: 160,
      render: (_, item) => <SText text={item.remark} />,
    },
    {
      title: <SText text={t("purchase.code")} />,
      dataIndex: "code",
      width: 100,
      hidden: !!supplier,
      render: (_, item) => <SText text={item?.supplier?.code ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("purchase.name")} />,
      dataIndex: "name",
      width: 140,
      hidden: !!supplier,
      render: (_, item) => (
        <SText text={item?.supplier?.name ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.shortName")} />,
      dataIndex: "shortName",
      width: 80,
      hidden: !!supplier,
      render: (_, item) => (
        <SText text={item?.supplier?.shortName ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.spareRate")} />,
      key: "spareRate",
      width: 130,
      render: (_, item) => <SText text={item?.spareRate ?? ""} suffix="%" />,
    },
    {
      title: <SText text={t("purchase.relatedDocuments")} />,
      key: "docId",
      fixed: 'right',
      width: 100,
      hidden: !!!supplier,
      render: (_, item) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={IconFolder}
            alt=""
            onClick={() => onOpenTab("priceDoc", { docId: item?.standPrice?.docId ?? '', shortName: supplier?.shortName ?? '', code: supplier?.code ?? '', proCode: item.product.code })}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      key: "action",
      fixed: 'right',
      width: 100,
      hidden: !!!supplier,
      render: (_, item) => (
        <Flex>
          <Button type="link" onClick={() => onDetail(item)}>
            {t("common.detail")}
          </Button>
          <Button type="link" danger onClick={() => onDel(item.priceId, item.id)}>
            {t("common.del")}
          </Button>
        </Flex>
      ),
    },
  ], [t, supplier?.taxRate]);

  return columns;
}

export default usePriceColumn;
